// src/pages/Auditorias/hooks/useConfigPrompts.js
import { useState, useEffect, useCallback } from 'react';
import { obterConfigPrompts, guardarConfigPrompt, guardarConfigDefinicoes } from '../api';

/**
 * Os três prompts do motor da auditoria, em edição.
 *
 * O servidor é a fonte da verdade e devolve tudo numa só chamada. Aqui guarda-se
 * uma cópia editável por origem (`rascunhos`) ao lado da versão gravada
 * (`origens`), para se saber o que está por gravar sem voltar ao servidor.
 *
 * Sem histórico: gravar substitui. Foi decisão do utilizador — a alternativa era
 * uma tabela config_prompt_versao, que não se justificou.
 */
export function useConfigPrompts() {
  const [origens, setOrigens] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [rascunhos, setRascunhos] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  // Modo por omissão das auditorias NOVAS. Não é rascunho: grava-se de imediato
  // ao mudar o toggle, porque não faz parte do par SYSTEM/USER de nenhuma origem.
  const [avaliarPecaIndividual, setAvaliarPecaIndividual] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await obterConfigPrompts();
      const lista = data.data?.origens || [];
      setOrigens(lista);
      setModelos(data.data?.modelos || []);
      setAvaliarPecaIndividual(data.data?.definicoes?.avaliar_peca_individual !== false);
      setRascunhos(Object.fromEntries(lista.map((o) => [o.origem, extrairEditavel(o)])));
      setErro(null);
    } catch (e) {
      console.error('Erro ao carregar prompts:', e);
      setErro(e.response?.data?.message || 'Não foi possível carregar os prompts');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const editar = useCallback((origem, campo, valor) => {
    setRascunhos((r) => ({ ...r, [origem]: { ...r[origem], [campo]: valor } }));
  }, []);

  const repor = useCallback((origem) => {
    const gravado = origens.find((o) => o.origem === origem);
    if (gravado) setRascunhos((r) => ({ ...r, [origem]: extrairEditavel(gravado) }));
  }, [origens]);

  /** Grava uma origem. Devolve a mensagem do servidor; propaga o erro ao chamador. */
  const guardar = useCallback(async (origem) => {
    const { data } = await guardarConfigPrompt(origem, rascunhos[origem]);
    const guardado = data.data;

    // Actualiza a versão gravada em memória para o "por gravar" desaparecer,
    // sem obrigar a uma segunda ida ao servidor
    setOrigens((lista) => lista.map((o) => (
      o.origem === origem
        ? { ...o, ...guardado, divergente: false }
        : o
    )));

    return data.message;
  }, [rascunhos]);

  /**
   * Muda o modo das auditorias novas. Optimista com reversão: o toggle mexe já
   * e volta atrás se o servidor recusar — sem isso ficava a mostrar um valor
   * que a base de dados não tem.
   */
  const definirAvaliarPecaIndividual = useCallback(async (valor) => {
    const anterior = avaliarPecaIndividual;
    setAvaliarPecaIndividual(valor);
    try {
      const { data } = await guardarConfigDefinicoes({ avaliar_peca_individual: valor });
      return data.message;
    } catch (e) {
      setAvaliarPecaIndividual(anterior);
      throw e;
    }
  }, [avaliarPecaIndividual]);

  return {
    origens, modelos, rascunhos, carregando, erro,
    avaliarPecaIndividual, definirAvaliarPecaIndividual,
    editar, repor, guardar, recarregar: carregar,
  };
}

/** Só os quatro campos que este ecrã edita. */
function extrairEditavel(origem) {
  return {
    texto_system: origem.texto_system || '',
    texto_user: origem.texto_user || '',
    modelo: origem.modelo || '',
    reasoning_effort: origem.reasoning_effort || '',
  };
}

/** True se o rascunho difere do que está gravado. */
export function temAlteracoes(gravado, rascunho) {
  if (!gravado || !rascunho) return false;
  const original = extrairEditavel(gravado);
  return Object.keys(original).some((campo) => original[campo] !== rascunho[campo]);
}
